import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LedgerJournal } from '../../../types/ledgerJournal';
import {
  eventColor,
  formatAmount,
  formatDateTime,
  formatSar,
  humanizeEvent,
  shortAccount,
  zohoStatusView,
} from '../journalExplorer.helpers';

export type JournalPartyKey = 'userId' | 'companyId' | 'employeeId';

interface JournalCardProps {
  journal: LedgerJournal;
  linesOpen: boolean;
  onPartyClick: (key: JournalPartyKey, value: number) => void;
}

const PARTY_LABELS: { key: JournalPartyKey; label: string }[] = [
  { key: 'companyId', label: 'Company' },
  { key: 'employeeId', label: 'Employee' },
  { key: 'userId', label: 'User' },
];

const formatMetaValue = (value: unknown) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};

const ZohoChip: React.FC<{ journal: LedgerJournal }> = ({ journal }) => {
  const { zoho } = journal;
  const view = zohoStatusView(zoho?.status);
  const details = zoho
    ? [
        zoho.zohoJournalId && `Zoho journal ${zoho.zohoJournalId}`,
        zoho.syncedAt && `Synced ${formatDateTime(zoho.syncedAt)}`,
        zoho.retryCount ? `Retries ${zoho.retryCount}` : '',
        zoho.lastError && `Last error: ${zoho.lastError}`,
      ].filter(Boolean)
    : ['This journal has no Zoho outbox row'];

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 420 }}>
          {details.map((line) => (
            <Typography key={String(line)} variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
              {line}
            </Typography>
          ))}
        </Box>
      }
    >
      <Chip
        size="small"
        color={view.color}
        variant={zoho?.status === 'SYNCED' ? 'outlined' : 'filled'}
        label={view.label}
      />
    </Tooltip>
  );
};

export const JournalCard: React.FC<JournalCardProps> = ({ journal, linesOpen, onPartyClick }) => {
  const theme = useTheme();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [linesVisible, setLinesVisible] = useState(linesOpen);

  React.useEffect(() => setLinesVisible(linesOpen), [linesOpen]);

  const color = eventColor(journal.event);
  const accent = color === 'default' ? theme.palette.grey[500] : theme.palette[color].main;
  const totalDebit = journal.lines.reduce((sum, line) => sum + line.debit, 0);
  const totalCredit = journal.lines.reduce((sum, line) => sum + line.credit, 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005;
  const metaEntries = Object.entries(journal.meta);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderLeft: `4px solid ${accent}`,
        overflow: 'hidden',
        opacity: journal.voided ? 0.6 : 1,
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
            <Chip size="small" color={color} label={humanizeEvent(journal.event)} sx={{ fontWeight: 600 }} />
            {journal.voided && <Chip size="small" color="error" variant="outlined" label="Voided" />}
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(journal.datetime)}
            </Typography>
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {journal.memo || '—'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {journal.referenceId}
            </Typography>
            <Tooltip title="Copy reference">
              <IconButton size="small" onClick={() => navigator.clipboard.writeText(journal.referenceId)}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {PARTY_LABELS.map(({ key, label }) => {
              const value = journal[key];

              return value ? (
                <Tooltip key={key} title={`Filter by ${label.toLowerCase()} ${value}`}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${label} #${value}`}
                    onClick={() => onPartyClick(key, value)}
                  />
                </Tooltip>
              ) : null;
            })}
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {formatSar(journal.amount)}
          </Typography>
          <ZohoChip journal={journal} />
          <Box>
            <Button size="small" onClick={() => setLinesVisible((open) => !open)}>
              {linesVisible ? 'Hide lines' : 'Show lines'}
            </Button>
            {!!metaEntries.length && (
              <Button
                size="small"
                endIcon={
                  <ExpandMoreIcon sx={{ transform: detailsOpen ? 'rotate(180deg)' : 'none', transition: '.2s' }} />
                }
                onClick={() => setDetailsOpen((open) => !open)}
              >
                Details
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Collapse in={linesVisible} unmountOnExit>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: 160 }}>
                Debit
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: 160 }}>
                Credit
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {journal.lines.map((line, index) => (
              // eslint-disable-next-line react/no-array-index-key -- lines have no id, and a journal can repeat an account
              <TableRow key={`${line.account}-${index}`} hover>
                <TableCell sx={{ pl: line.credit > 0 ? 5 : 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {line.zohoAccount || shortAccount(line.account)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {line.account}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'success.main' }}>
                  {formatAmount(line.debit)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'error.main' }}>
                  {formatAmount(line.credit)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: 'action.selected' }}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={700}>
                    Total
                  </Typography>
                  {balanced ? (
                    <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Balanced" />
                  ) : (
                    <Chip
                      size="small"
                      color="error"
                      icon={<ErrorIcon />}
                      label={`Off by ${formatAmount(Math.abs(totalDebit - totalCredit))}`}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {formatAmount(totalDebit)}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {formatAmount(totalCredit)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Collapse>

      <Collapse in={detailsOpen} unmountOnExit>
        <Box
          sx={{
            p: 2,
            bgcolor: 'action.hover',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 1.5,
          }}
        >
          {journal.transactionId && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                transactionId
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {journal.transactionId}
              </Typography>
            </Box>
          )}
          {metaEntries.map(([key, value]) => (
            <Box key={key}>
              <Typography variant="caption" color="text.secondary">
                {key}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {formatMetaValue(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};
